// Input: keyboard + pallasite-style virtual joystick, beam and special buttons.
// iOS-safe: gesture tracked on window, deliberately NO setPointerCapture
// (iOS half-holds capture and swallows pointerup, leaving a stuck stick).

import { unlockAudio } from './audio';

export interface InputState {
  x: number;      // -1..1 (screen right positive)
  z: number;      // -1..1 (screen down positive)
  beam: boolean;
  special: boolean;
}

// Keep the knob inside its drawn gate and reach full thrust before the pilot's
// thumb reaches the rim. The ease-out response preserves a small precision
// zone, but a natural 24px push now gives the decisive three-quarter thrust a
// keyboard pilot gets without having to drag their thumb to the edge.
const MAX_RADIUS = 44;
const DEADZONE = 0.08;

export const input: InputState = { x: 0, z: 0, beam: false, special: false };

const keys = new Set<string>();
let stickX = 0;
let stickZ = 0;
let beamHeld = false;
let beamKey = false;
let specialHeld = false;
let padX = 0;
let padZ = 0;
let padBeam = false;
let padSpecial = false;
let specialQueued = false;
let resetPointerVisual: () => void = () => undefined;

function recompute(): void {
  let kx = 0;
  let kz = 0;
  if (keys.has('ArrowLeft') || keys.has('KeyA')) kx -= 1;
  if (keys.has('ArrowRight') || keys.has('KeyD')) kx += 1;
  if (keys.has('ArrowUp') || keys.has('KeyW')) kz -= 1;
  if (keys.has('ArrowDown') || keys.has('KeyS')) kz += 1;
  const klen = Math.hypot(kx, kz);
  if (klen > 1) { kx /= klen; kz /= klen; }
  const useStick = Math.abs(stickX) > 0.001 || Math.abs(stickZ) > 0.001;
  const usePad = !useStick && (Math.abs(padX) > 0.001 || Math.abs(padZ) > 0.001);
  input.x = useStick ? stickX : usePad ? padX : kx;
  input.z = useStick ? stickZ : usePad ? padZ : kz;
  input.beam = beamHeld || beamKey || padBeam;
  const nextSpecial = specialHeld || keys.has('ShiftLeft') || keys.has('ShiftRight') || padSpecial;
  if (nextSpecial && !input.special) specialQueued = true;
  input.special = nextSpecial;
}

/** A press survives a down+up between render frames and is consumed once. */
export function consumeSpecialPress(): boolean {
  const queued = specialQueued;
  specialQueued = false;
  return queued;
}

/**
 * Drop every held control after focus or visibility is interrupted. Browsers
 * are not required to deliver the matching keyup/pointerup, and gamepad state
 * must not survive the same interruption either.
 */
export function releaseAllInput(): void {
  keys.clear();
  stickX = 0;
  stickZ = 0;
  beamHeld = false;
  beamKey = false;
  specialHeld = false;
  padX = 0;
  padZ = 0;
  padBeam = false;
  padSpecial = false;
  specialQueued = false;
  resetPointerVisual();
  recompute();
}

/** Fed by the gamepad poller each frame; touch stick wins if both are live. */
export function setGamepadState(x: number, z: number, beam: boolean, special = false): void {
  if (x === padX && z === padZ && beam === padBeam && special === padSpecial) return;
  padX = x;
  padZ = z;
  padBeam = beam;
  padSpecial = special;
  recompute();
}

export function initInput(): void {
  window.addEventListener('keydown', (e) => {
    if (e.repeat) return;
    if (e.code === 'Space') { beamKey = true; e.preventDefault(); }
    keys.add(e.code);
    recompute();
  });
  window.addEventListener('keyup', (e) => {
    if (e.code === 'Space') beamKey = false;
    keys.delete(e.code);
    recompute();
  });
  window.addEventListener('blur', releaseAllInput);

  const touchRoot = document.getElementById('touch')!;
  const pad = document.getElementById('pad')!;
  const knob = document.getElementById('knob')!;
  const beamBtn = document.getElementById('beam-btn')!;
  const specialBtn = document.getElementById('special-btn')!;

  // Reveal touch controls on first real touch.
  window.addEventListener('touchstart', () => { touchRoot.hidden = false; }, { once: true, passive: true });
  if (matchMedia('(pointer: coarse)').matches) touchRoot.hidden = false;

  let activeId: number | null = null;
  let padCx = 0;
  let padCy = 0;

  const applyFinger = (clientX: number, clientY: number) => {
    const dx = clientX - padCx;
    const dy = clientY - padCy;
    const dist = Math.hypot(dx, dy) || 1;
    const clipped = Math.min(dist, MAX_RADIUS);
    knob.style.transform = `translate(${(dx / dist) * clipped}px, ${(dy / dist) * clipped}px)`;
    const mag = clipped / MAX_RADIUS;
    if (mag > DEADZONE) {
      const scaled = (mag - DEADZONE) / (1 - DEADZONE);
      const response = scaled * (2 - scaled);
      stickX = (dx / dist) * response;
      stickZ = (dy / dist) * response;
    } else {
      stickX = 0;
      stickZ = 0;
    }
    recompute();
  };

  const endGesture = (e?: PointerEvent) => {
    if (e && activeId !== null && e.pointerId !== activeId) return;
    activeId = null;
    stickX = 0;
    stickZ = 0;
    knob.style.transform = 'translate(0px, 0px)';
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onEnd);
    window.removeEventListener('pointercancel', onEnd);
    recompute();
  };
  const onMove = (e: PointerEvent) => {
    if (e.pointerId !== activeId) return;
    e.preventDefault();
    applyFinger(e.clientX, e.clientY);
  };
  const onEnd = (e: PointerEvent) => endGesture(e);
  resetPointerVisual = endGesture;

  pad.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    unlockAudio();
    activeId = e.pointerId;
    const rect = pad.getBoundingClientRect();
    padCx = rect.left + rect.width / 2;
    padCy = rect.top + rect.height / 2;
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    applyFinger(e.clientX, e.clientY);
  });

  const beamOn = (e: PointerEvent) => {
    e.preventDefault();
    unlockAudio();
    beamHeld = true;
    recompute();
  };
  const beamOff = () => {
    beamHeld = false;
    recompute();
  };
  beamBtn.addEventListener('pointerdown', beamOn);
  beamBtn.addEventListener('pointerup', beamOff);
  beamBtn.addEventListener('pointercancel', beamOff);
  beamBtn.addEventListener('pointerleave', beamOff);

  const specialOn = (e: PointerEvent) => {
    e.preventDefault();
    unlockAudio();
    specialHeld = true;
    recompute();
  };
  const specialOff = () => {
    specialHeld = false;
    recompute();
  };
  specialBtn.addEventListener('pointerdown', specialOn);
  specialBtn.addEventListener('pointerup', specialOff);
  specialBtn.addEventListener('pointercancel', specialOff);
  specialBtn.addEventListener('pointerleave', specialOff);

  document.addEventListener('contextmenu', (e) => {
    if ((e.target as HTMLElement).closest('#touch')) e.preventDefault();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) releaseAllInput();
  });
}
